import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import { loadSeoSettings, getSeoConfig, subscribeSeo } from "./lib/seo";
import { GAME_ROUTES } from "./lib/gameRoutes";

const TestPage = lazy(() => import("./pages/TestPage"));
const ReactionLab = lazy(() => import("./pages/ReactionLab"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Daily = lazy(() => import("./pages/Daily"));
const Challenge = lazy(() => import("./pages/Challenge"));
const Profile = lazy(() => import("./pages/Profile"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Learn = lazy(() => import("./pages/Learn"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Methodology = lazy(() => import("./pages/Methodology"));
const Statistics = lazy(() => import("./pages/Statistics"));
const StatisticsDistribution = lazy(() => import("./pages/StatisticsDistribution"));
const SeoPage = lazy(() => import("./pages/SeoPage"));
const MsReactionPage = lazy(() => import("./pages/SeoPage").then((m) => ({ default: m.MsReactionPage })));
const ReactionGames = lazy(() => import("./pages/ReactionGames"));
const BrainHub = lazy(() => import("./pages/BrainHub"));
const SeoBrainPage = lazy(() => import("./pages/SeoBrainPage"));
const SpeedTests = lazy(() => import("./pages/SpeedTests"));
const SeoGamePage = lazy(() => import("./pages/SeoGamePage"));
const AverageReactionTime = lazy(() => import("./pages/guides/AverageReactionTime"));
const ReactionTimeByAge = lazy(() => import("./pages/guides/ReactionTimeByAge"));
const ImproveReactionTime = lazy(() => import("./pages/guides/ImproveReactionTime"));
const Gaming = lazy(() => import("./pages/Gaming"));
const Guides = lazy(() => import("./pages/Guides"));
const Education = lazy(() => import("./pages/Education"));
const TournamentPage = lazy(() => import("./pages/TournamentPage"));
const EmbedTest = lazy(() => import("./pages/EmbedTest"));
const EmbedHub = lazy(() => import("./pages/EmbedHub"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="page-loader">
      <div className="w-10 h-10 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Layout() {
  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="relative z-10">
        <Suspense fallback={<PageLoader />}><Outlet /></Suspense>
      </div>
      <Footer />
    </div>
  );
}

function AuthCallback() {
  const { loginGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { navigate("/"); return; }
    (async () => {
      try {
        await loginGoogle(sid);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/60">Signing you in…</p>
      </div>
    </div>
  );
}

function AnalyticsLoader() {
  const [cfg, setCfg] = useState(getSeoConfig());
  useEffect(() => {
    loadSeoSettings();
    return subscribeSeo(() => setCfg(getSeoConfig()));
  }, []);
  useEffect(() => {
    if (!cfg.ga || document.getElementById("ga-script")) return;
    const s = document.createElement("script");
    s.id = "ga-script"; s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${cfg.ga}`;
    document.head.appendChild(s);
    const inline = document.createElement("script");
    inline.id = "ga-inline";
    inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${cfg.ga}');`;
    document.head.appendChild(inline);
  }, [cfg.ga]);
  useEffect(() => {
    if (!cfg.gsc) return;
    let m = document.querySelector('meta[name="google-site-verification"]');
    if (!m) { m = document.createElement("meta"); m.name = "google-site-verification"; document.head.appendChild(m); }
    m.content = cfg.gsc;
  }, [cfg.gsc]);
  return null;
}

const SEO_PAGE_SLUGS = ["reaction-time-test", "reaction-time-test-online", "human-reaction-time", "average-reaction-time", "reaction-time-by-age", "fastest-reaction-time", "visual-reaction-test", "audio-reaction-test", "gamer-reaction-time", "sports-reaction-test", "reflex-test", "reaction-speed-test", "how-to-improve-reaction-time"];
const SEO_BRAIN_SLUGS = ["brain-games", "memory-test", "sequence-memory-test", "number-memory-test", "verbal-memory-test", "chimp-test", "visual-memory-test", "aim-test", "typing-test"];
const SEO_GAME_SLUGS = ["peripheral-vision-test", "hand-eye-coordination-test", "schulte-table", "reflex-timing-game", "reaction-rhythm-game", "reflex-runner-game", "click-speed-test", "cps-test", "spacebar-speed-test", "jitter-click-test", "butterfly-click-test", "right-click-cps-test", "left-click-cps-test", "kohi-click-test", "aim-trainer", "f1-reaction-test", "quick-draw-game", "rhythm-memory-game"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {Object.entries(GAME_ROUTES).map(([path, Game]) => <Route key={path} path={path} element={<Game />} />)}
        <Route path="/test/:type" element={<TestPage />} />
        <Route path="/tournament" element={<TournamentPage />} />
        <Route path="/embed/:type" element={<EmbedTest />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/tests" element={<ReactionLab />} />
          <Route path="/games" element={<ReactionGames />} />
          <Route path="/brain" element={<BrainHub />} />
          {SEO_BRAIN_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<SeoBrainPage slug={s} />} />)}
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/statistics/reaction-time-distribution" element={<StatisticsDistribution />} />
          <Route path="/leaderboard/:country" element={<Leaderboard />} />
          <Route path="/daily" element={<Daily />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/challenge/:code" element={<Challenge />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/methodology" element={<Methodology />} />
          {SEO_PAGE_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<SeoPage slug={s} />} />)}
          <Route path="/speed-tests" element={<SpeedTests />} />
          {SEO_GAME_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<SeoGamePage slug={s} />} />)}
          <Route path="/guides/average-reaction-time" element={<AverageReactionTime />} />
          <Route path="/guides/reaction-time-by-age" element={<ReactionTimeByAge />} />
          <Route path="/guides/how-to-improve-reaction-time" element={<ImproveReactionTime />} />
          <Route path="/gaming" element={<Gaming />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/education" element={<Education />} />
          <Route path="/embed" element={<EmbedHub />} />
          <Route path="/reaction-time/:ms" element={<MsReactionPage />} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <AnalyticsLoader />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
